/**
 * What the board draws around a ship that may sail (docs/ux.md U-MOVE-02): along each of the
 * eight rays, the cells it can reach, the cells the wind would allow but something blocks, and
 * the farthest distance on the ray. Pure; reach itself comes from the rules engine.
 */
import {
  DIRECTION_VECTORS,
  DIRECTIONS,
  explain,
  maxDistance,
  parseCellId,
  rayBetween,
  toCellId,
  type Action,
  type Direction,
  type GameState,
  type Point,
  type Reason,
} from '@brig/rules-engine';

const step = (p: Point, d: Direction, n: number): Point => ({
  x: p.x + DIRECTION_VECTORS[d].x * n,
  y: p.y + DIRECTION_VECTORS[d].y * n,
});

export interface Reach {
  /** Cells within the wind's range on a ray that the ship can't sail to (land, reef, full…). */
  readonly blocked: ReadonlySet<string>;
}

const NO_REACH: Reach = { blocked: new Set() };

/**
 * The rays of `shipId` under the cast wind, given the cells the engine lets it reach.
 * Outside battle (no wind yet) or for a ship that can't move there is nothing to draw.
 */
export function reachOf(state: GameState, shipId: string, moveCells: ReadonlySet<string>): Reach {
  const ship = state.ships.find((s) => s.id === shipId);
  const from = ship ? parseCellId(ship.cell) : null;
  if (!ship || !from || moveCells.size === 0 || !state.turn.spin) return NO_REACH;
  const blocked = new Set<string>();
  for (const d of DIRECTIONS) {
    const n = maxDistance(state, ship, d);
    for (let i = 1; i <= n; i++) {
      const p = step(from, d, i);
      if (p.x < 0 || p.y < 0 || p.x >= state.map.width || p.y >= state.map.height) break;
      const cell = toCellId(p);
      if (!moveCells.has(cell)) blocked.add(cell);
    }
  }
  return { blocked };
}

/** The ray from a ship to a cell, if the cell lies on one of its eight lines. */
export function rayTo(state: GameState, shipId: string, cell: string): { d: Direction; n: number } | null {
  const ship = state.ships.find((s) => s.id === shipId);
  const a = ship ? parseCellId(ship.cell) : null;
  const b = parseCellId(cell);
  if (!a || !b || (a.x === b.x && a.y === b.y)) return null;
  const ray = rayBetween(a, b);
  return ray?.direction ? { d: ray.direction, n: ray.distance } : null;
}

/** The cells a move passes through, the ship's own cell first: the route preview (U-MOVE-02). */
export function routeTo(state: GameState, shipId: string, cell: string): string[] {
  const ray = rayTo(state, shipId, cell);
  const ship = state.ships.find((s) => s.id === shipId);
  const from = ship ? parseCellId(ship.cell) : null;
  if (!ray || !from) return [];
  return Array.from({ length: ray.n + 1 }, (_, i) => toCellId(step(from, ray.d, i)));
}

/** Why the selected ship can't sail to `cell`, asked of the engine (U-WHY-02, U-WHY-04). */
export function whyNotThere(state: GameState, shipId: string, cell: string): Reason | null {
  const action: Action = { type: 'moveShip', side: state.turn.side, shipId, to: cell };
  return explain(state, action)?.reason ?? null;
}
